import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { sectionToHtml, END_CLASSES } from "@/utils/sectionMarkup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { page_path: "", heading: "", heading_level: "h2", body: "", anchor_text: "", placement: "after", reason: "" };

const PLACEMENTS = [
  { value: "after", label: "After this text" },
  { value: "before", label: "Before this text" },
  { value: "end", label: "End of page" },
];

function StatusBadge({ s }) {
  if (s.pending_update) return <Badge variant="outline" className="text-amber-700 border-amber-300"><Clock className="w-3 h-3 mr-1" /> Live · edit awaiting approval</Badge>;
  if (s.status === "approved") return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle2 className="w-3 h-3 mr-1" /> Live</Badge>;
  if (s.status === "rejected") return <Badge variant="outline" className="text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
  return <Badge variant="outline" className="text-amber-700 border-amber-300"><Clock className="w-3 h-3 mr-1" /> Awaiting approval</Badge>;
}

function SectionPreview({ form }) {
  if (!form.heading.trim() && !form.body.trim()) return null;
  return (
    <div className="border border-dashed border-[#CBD5E1] rounded p-4 bg-white" data-testid="section-preview">
      <p className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-3">Preview</p>
      <div dangerouslySetInnerHTML={{ __html: sectionToHtml(form, END_CLASSES) }} />
    </div>
  );
}

function SectionForm({ form, setForm, editingId, saving, onSubmit, onCancel }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="section-form">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3">
        <div>
          <Label className="text-xs">Page path</Label>
          <Input value={form.page_path} onChange={set("page_path")} placeholder="e.g. /solutions/it-tech-offices" data-testid="section-page-path" />
        </div>
        <div>
          <Label className="text-xs">Headline size</Label>
          <select value={form.heading_level} onChange={set("heading_level")} className="mt-0 w-full h-9 border border-input rounded-md px-2 text-sm bg-white" data-testid="section-heading-level">
            <option value="h2">H2 (main)</option>
            <option value="h3">H3 (sub)</option>
          </select>
        </div>
      </div>
      <div>
        <Label className="text-xs">Headline</Label>
        <Input value={form.heading} onChange={set("heading")} placeholder="e.g. Office Furniture for IT Companies in Mumbai" data-testid="section-heading" />
      </div>
      <div>
        <Label className="text-xs">Content</Label>
        <Textarea rows={8} value={form.body} onChange={set("body")} data-testid="section-body"
          placeholder={"Write your paragraph here.\n\nLeave a blank line to start a new paragraph.\n- Lines starting with a dash become bullet points\n- **bold text** and [link text](/products) also work"} />
        <p className="text-[10px] text-slate-500 mt-1">Blank line = new paragraph · <code>- item</code> = bullet · <code>**bold**</code> · <code>[anchor text](/page-path)</code> = internal link</p>
      </div>
      <div>
        <Label className="text-xs">Where on the page?</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {PLACEMENTS.map((p) => (
            <button key={p.value} type="button" onClick={() => setForm({ ...form, placement: p.value })}
              className={`text-xs px-3 py-1.5 rounded border transition-colors ${form.placement === p.value ? "bg-[#F97316] text-white border-[#F97316]" : "bg-white border-[#E2E8F0] text-[#334155] hover:bg-[#F1F5F9]"}`}
              data-testid={`section-placement-${p.value}`}>{p.label}</button>
          ))}
        </div>
      </div>
      {form.placement !== "end" && (
        <div>
          <Label className="text-xs">Existing text on the page (paste a paragraph or headline)</Label>
          <Textarea rows={2} value={form.anchor_text} onChange={set("anchor_text")} data-testid="section-anchor-text"
            placeholder="Copy a sentence from the live page; the new section is placed right next to that block" />
          <p className="text-[10px] text-slate-500 mt-1">If this text can't be found on the page, the section is shown at the end of the page instead.</p>
        </div>
      )}
      <div>
        <Label className="text-xs">SEO reasoning <span className="text-slate-400">(optional)</span></Label>
        <Input value={form.reason} onChange={set("reason")} placeholder="e.g. Targets 'IT office furniture Mumbai' keyword" data-testid="section-reason" />
      </div>
      <SectionPreview form={form} />
      <div className="flex gap-2">
        <Button type="submit" disabled={saving} className="bg-[#F97316] hover:bg-[#EA7515] text-white" data-testid="section-submit-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? "Save changes" : <><Plus className="w-4 h-4 mr-1" /> Add section</>}
        </Button>
        {editingId && <Button type="button" variant="outline" onClick={onCancel} data-testid="section-cancel-edit">Cancel</Button>}
      </div>
    </form>
  );
}

function SectionRow({ s, onEdit, onDelete }) {
  const preview = () => window.open(`${window.location.origin}${s.page_path}?_seo_preview=${Date.now()}`, "_blank", "noopener,noreferrer");
  return (
    <div className="border border-[#E2E8F0] rounded p-3" data-testid={`section-row-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-[#64748B]">{s.page_path} · {s.placement === "end" ? "end of page" : `${s.placement} "${(s.anchor_text || "").slice(0, 50)}"`}</div>
          <div className="font-semibold text-sm text-[#0F172A] truncate">{s.heading}</div>
        </div>
        <StatusBadge s={s} />
      </div>
      {s.decision_notes && s.status === "rejected" && <div className="text-xs mt-2 p-2 bg-[#F1F5F9] rounded"><strong>Owner notes:</strong> {s.decision_notes}</div>}
      <div className="flex gap-2 mt-2">
        <Button size="sm" variant="outline" onClick={() => onEdit(s)} data-testid={`section-edit-${s.id}`}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
        <Button size="sm" variant="outline" onClick={() => onDelete(s)} className="text-red-600 border-red-300 hover:bg-red-50" data-testid={`section-delete-${s.id}`}><Trash2 className="w-3 h-3 mr-1" /> Remove</Button>
        {s.status === "approved" && <Button size="sm" variant="ghost" onClick={preview} className="text-[#F97316]" data-testid={`section-preview-${s.id}`}><ExternalLink className="w-3 h-3 mr-1" /> Live Preview</Button>}
      </div>
    </div>
  );
}

export default function SectionsEditor({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = () => axios.get(`${API}/seo/sections`, { headers: authHeaders() }).then((r) => setItems(r.data || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  const reset = () => { setForm(EMPTY); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.page_path.trim() || !form.heading.trim() || !form.body.trim()) { toast.error("Page path, headline and content are required"); return; }
    if (form.placement !== "end" && !form.anchor_text.trim()) { toast.error("Paste the existing text to place the section next to, or choose 'End of page'"); return; }
    setSaving(true);
    try {
      const res = editingId
        ? await axios.put(`${API}/seo/sections/${editingId}`, form, { headers: authHeaders() })
        : await axios.post(`${API}/seo/sections`, form, { headers: authHeaders() });
      const st = res.data.status;
      if (st === "approved") toast.success("Section is live on the page now.");
      else toast.warning("Sent for owner approval (top-5 page). It goes live once approved.", { duration: 6000 });
      (res.data.compliance_replacements || []).forEach((r) => toast.info(`Auto-fix: "${r.original}" → "${r.replacement}"`, { duration: 8000 }));
      reset(); refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString() || "Save failed");
    } finally { setSaving(false); }
  };

  const edit = (s) => {
    const src = s.pending_update || s;
    setForm({ page_path: src.page_path, heading: src.heading, heading_level: src.heading_level, body: src.body, anchor_text: src.anchor_text || "", placement: src.placement, reason: src.reason || "" });
    setEditingId(s.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (s) => {
    if (!window.confirm(`Remove the section "${s.heading}" from ${s.page_path}?`)) return;
    try {
      await axios.delete(`${API}/seo/sections/${s.id}`, { headers: authHeaders() });
      toast.success("Section removed from the page.");
      if (editingId === s.id) reset();
      refresh();
    } catch (err) { toast.error("Remove failed"); }
  };

  return (
    <div className="space-y-6" data-testid="sections-editor">
      <div className="bg-green-50 border-2 border-green-300 p-4 rounded text-sm text-green-900">
        <strong className="block mb-1">Add a brand-new section (headline + paragraphs + bullets) to any page.</strong>
        Non-gated pages go live instantly. The 5 gated pages (<code>/</code>, <code>/about</code>, <code>/products</code>, <code>/contact</code>, <code>/modular-office-workstations-mumbai</code>) need owner approval first. All text is compliance-scrubbed and audit-logged.
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">{editingId ? "Edit Section" : "Add New Section"}</CardTitle></CardHeader>
        <CardContent>
          <SectionForm form={form} setForm={setForm} editingId={editingId} saving={saving} onSubmit={submit} onCancel={reset} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Your Sections ({items.length})</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {items.length === 0 ? <p className="text-sm text-[#94A3B8] py-4 text-center">No sections added yet.</p>
            : items.map((s) => <SectionRow key={s.id} s={s} onEdit={edit} onDelete={del} />)}
        </CardContent>
      </Card>
    </div>
  );
}
